import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DebtTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload || {};
  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-md px-3 py-2 text-xs font-mono">
      <div className="font-semibold text-zinc-800 mb-1">{label}</div>
      <div className="text-amber-700">Dette salaires : {fmt(p.debt_salaries_mad)} MAD</div>
      <div className="text-amber-500">Dette charges : {fmt(p.debt_charges_mad)} MAD</div>
      <div className="font-semibold text-zinc-900 border-t border-zinc-100 mt-1 pt-1">Total : {fmt(p.debt_total_mad)} MAD</div>
      <div className="text-zinc-500 mt-1">Née ce mois : +{fmt(p.born_mad)} · Payée : −{fmt(p.paid_mad)}</div>
      <div className="text-zinc-400 mt-1">Cliquer pour le détail →</div>
    </div>
  );
};

const DetailSection = ({ title, items, total, tone, testid }) => (
  <div className={`border rounded-md overflow-hidden ${tone === "salary" ? "border-amber-300" : "border-orange-200"}`} data-testid={testid}>
    <div className={`px-3 py-1.5 text-xs font-semibold flex justify-between ${tone === "salary" ? "bg-amber-50 text-amber-900" : "bg-orange-50 text-orange-900"}`}>
      <span>{title}</span>
      <span className="font-mono">{fmt(total)} MAD</span>
    </div>
    {items.length === 0 ? (
      <div className="px-3 py-2 text-xs text-zinc-400">Aucune.</div>
    ) : (
      <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-zinc-500 font-mono uppercase tracking-wider text-[10px] border-b border-zinc-100">
            <th className="text-left px-3 py-1">Collaborateur</th>
            <th className="text-left px-2 py-1">Mois d&apos;origine</th>
            <th className="text-right px-2 py-1">Dette née</th>
            <th className="text-right px-2 py-1">Déjà payé</th>
            <th className="text-right px-3 py-1">Restant</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {items.map((it, i) => (
            <tr key={i} data-testid={`debt-detail-row-${tone}-${i}`}>
              <td className="px-3 py-1">{it.user_name || "—"}</td>
              <td className="px-2 py-1 font-mono text-zinc-500">{it.origin}</td>
              <td className="px-2 py-1 text-right font-mono text-zinc-500">{fmt(it.amount_mad)}</td>
              <td className="px-2 py-1 text-right font-mono text-emerald-700">{it.settled_mad > 0 ? `−${fmt(it.settled_mad)}` : "—"}</td>
              <td className="px-3 py-1 text-right font-mono font-semibold">{fmt(it.remaining_mad)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    )}
  </div>
);

const DebtDetailDialog = ({ ym, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!ym) return;
    setDetail(null);
    let live = true;
    const [y, m] = ym.split("-").map(Number);
    api.get(`/admin/payroll-liabilities/detail?year=${y}&month=${m}`)
      .then((r) => { if (live) setDetail(r.data); })
      .catch(() => { if (live) setDetail({ error: true }); });
    return () => { live = false; };
  }, [ym, reload]);
  return (
    <Dialog open={!!ym} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="debt-detail-modal">
        <DialogHeader>
          <DialogTitle>Composition de la dette — fin {ym}</DialogTitle>
          <DialogDescription className="text-xs">
            Salaires impayés et charges sociales différées encore dus à la fin de ce mois.
          </DialogDescription>
        </DialogHeader>
        {!detail ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-zinc-400" size={20} /></div>
        ) : detail.error ? (
          <div className="text-sm text-rose-600 py-4 text-center">Détail indisponible.</div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs font-mono text-zinc-600 flex flex-wrap gap-x-4" data-testid="debt-detail-totals">
              <span>Salaires impayés : <b className="text-amber-800">{fmt(detail.total_salary_mad)} MAD</b></span>
              <span>Charges différées : <b className="text-orange-700">{fmt(detail.total_charges_mad)} MAD</b></span>
              <span>Total : <b className="text-zinc-900">{fmt(detail.total_mad)} MAD</b></span>
            </div>
            <DetailSection title="Salaires impayés (net)" items={detail.salary || []} total={detail.total_salary_mad} tone="salary" testid="debt-detail-salaries" />
            <DetailSection title="Charges sociales différées (CNSS / AMO / IR)" items={detail.charges || []} total={detail.total_charges_mad} tone="charges" testid="debt-detail-charges" />
            {(detail.paid_in_month || []).length > 0 && (
              <div className="border border-emerald-200 rounded-md overflow-hidden" data-testid="debt-detail-paid">
                <div className="px-3 py-1.5 text-xs font-semibold flex justify-between bg-emerald-50 text-emerald-900">
                  <span>Payé pendant {ym}</span>
                  <span className="font-mono">−{fmt(detail.total_paid_in_month_mad)} MAD</span>
                </div>
                <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-zinc-100">
                    {detail.paid_in_month.map((p, i) => (
                      <tr key={i}>
                        <td className="px-3 py-1 font-mono text-zinc-500 whitespace-nowrap">{p.date || "—"}</td>
                        <td className="px-2 py-1">{p.kind === "salary" ? `Salaire ${p.user_name || ""}` : `Charges ${p.user_name || ""}`} <span className="text-zinc-400 font-mono">(origine {p.origin})</span></td>
                        <td className="px-2 py-1 text-zinc-500 truncate max-w-[200px]">{p.label}</td>
                        <td className="px-3 py-1 text-right font-mono text-emerald-700 whitespace-nowrap">−{fmt(p.amount_mad)}</td>
                        <td className="px-2 py-1 w-16 text-right">
                          {p.declaration_id && (
                            <button type="button" title="Annuler cette déclaration « charges à jour » : la dette redevient due"
                              data-testid={`debt-undo-declaration-${p.declaration_id}-${i}`}
                              onClick={async () => {
                                if (!window.confirm("Annuler cette déclaration ? Les charges soldées par elle redeviendront dues.")) return;
                                try {
                                  await api.delete(`/admin/payroll-liabilities/declarations/${p.declaration_id}`);
                                  toast.success("Déclaration annulée");
                                  setReload((n) => n + 1);
                                } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
                              }}
                              className="px-1.5 py-0.5 rounded text-[10px] font-mono uppercase border border-zinc-200 text-zinc-600 hover:bg-zinc-100 transition-colors">
                              Annuler
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const DebtTimeline = ({ year, month }) => {
  const [points, setPoints] = useState(null);
  const [detailYm, setDetailYm] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/admin/payroll-liabilities/timeline?year=${year}&month=${month}&months=12`)
      .then((r) => { if (live) setPoints(r.data?.points || []); })
      .catch(() => { if (live) setPoints([]); });
    return () => { live = false; };
  }, [year, month]);

  if (!points || !points.some((p) => p.debt_total_mad > 0 || p.paid_mad > 0)) return null;
  return (
    <div className="border border-amber-200 bg-amber-50/30 rounded-lg p-4" data-testid="debt-timeline">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <div>
          <div className="text-sm font-semibold text-zinc-900">Évolution de la dette sociale</div>
          <div className="text-[10px] uppercase font-mono tracking-wider text-zinc-500 mt-0.5">
            Dette restante à fin de mois — 12 derniers mois (salaires impayés + charges différées) · cliquer sur un mois pour le détail
          </div>
        </div>
        <div className="text-xs font-mono text-amber-800" data-testid="debt-timeline-current">
          Fin {points[points.length - 1]?.ym} : {fmt(points[points.length - 1]?.debt_total_mad)} MAD
        </div>
      </div>
      <div className="h-48 min-h-[192px] min-w-[200px]">
        <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={192}>
          <BarChart
            data={points}
            margin={{ top: 4, right: 8, left: 8, bottom: 0 }}
            onClick={(st) => { if (st?.activeLabel) setDetailYm(st.activeLabel); }}
            style={{ cursor: "pointer" }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0e7d8" vertical={false} />
            <XAxis dataKey="ym" tick={{ fontSize: 10, fontFamily: "monospace" }} tickLine={false} axisLine={{ stroke: "#e4d9c3" }} />
            <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} tickLine={false} axisLine={false} width={70}
              tickFormatter={(v) => Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} />
            <Tooltip content={<DebtTooltip />} cursor={{ fill: "rgba(217,119,6,0.06)" }} />
            <Bar dataKey="debt_salaries_mad" stackId="d" fill="#b45309" name="Dette salaires" radius={[0, 0, 0, 0]} />
            <Bar dataKey="debt_charges_mad" stackId="d" fill="#f59e0b" name="Dette charges" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-4 mt-1 text-[10px] font-mono text-zinc-600">
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: "#b45309" }} /> Salaires impayés</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: "#f59e0b" }} /> Charges différées (CNSS / AMO / IR)</span>
      </div>
      <DebtDetailDialog ym={detailYm} onClose={() => setDetailYm(null)} />
    </div>
  );
};

export default DebtTimeline;
